import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ODDS_HISTORY_API_URL } from '../config';
import { useOddsFormat, fmtPrice } from '../oddsFormat';

// Same host as odds-history; swap the path so no config.js change is needed.
const ALT_ODDS_API_URL = ODDS_HISTORY_API_URL.replace(/odds-history\/?$/, 'alt-odds');

// Modal content area is a fixed height for both tabs, so switching tabs never resizes it.
// Default row counts are sized so the collapsed view fits that height without scrolling;
// only "Show all" introduces scrolling (inside the table — the chart stays pinned).
const CONTENT_HEIGHT = 'min(680px, calc(85vh - 210px))';   // 85vh dialog cap minus header + tabs
// Phones in either orientation get a full-screen sheet; the fixed height above
// would leave ~120px for content on a phone turned sideways.
const PHONE_MQ = '(max-width:600px), (max-height:500px)';
const HISTORY_ROWS = 12;                  // fits under the chart
const ALT_WINDOW = 8;                     // rungs each side of the clicked line
const ALT_ROWS = ALT_WINDOW * 2 + 1;       // 17 — no chart on this tab, so more rows fit

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');

// ══════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const month = String(d.getMonth() + 1).padStart(2, '\u00A0');
  const day = String(d.getDate()).padStart(2, '\u00A0');
  const time = d.toLocaleString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  });
  return `${month}/${day} ${time}`;
};

const fmtEventStart = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).replace(',', '');
};

// ══════════════════════════════════════════════════════════
//  HOOK
// ══════════════════════════════════════════════════════════

export function useOddsHistory() {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyMeta, setHistoryMeta] = useState(null);

  const openOddsHistory = useCallback(async (meta, bookName) => {
    if (!meta) return;
    setHistoryMeta({ ...meta, bookName });
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const params = new URLSearchParams({
        fixture_id: meta.fixture_id || '',
        sportsbook: bookName || '',
        market_id: meta.market_id || '',
        selection: meta.selection || '',
        selection_line: meta.selection_line || '',
        player_id: meta.player_id || '',
      });
      const resp = await fetch(`${ODDS_HISTORY_API_URL}?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setHistoryData(data.history || []);
      }
    } catch (err) {
      console.error('[ODDS-HISTORY] Fetch error:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const closeOddsHistory = useCallback(() => setHistoryOpen(false), []);

  return { historyOpen, historyData, historyLoading, historyMeta, openOddsHistory, closeOddsHistory };
}

// ══════════════════════════════════════════════════════════
//  MODAL COMPONENT
// ══════════════════════════════════════════════════════════

export default function OddsHistoryModal({ open, onClose, data, loading, meta }) {
  // Hook must run before the early return (rules of hooks).
  const oddsFormat = useOddsFormat();
  const [tab, setTab] = useState('history');
  const [alts, setAlts] = useState(null);          // { rungs, mine_label, other_label } | null
  const [altsLoading, setAltsLoading] = useState(false);
  const [altsError, setAltsError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const phone = useMediaQuery(PHONE_MQ);

  const altKey = meta
    ? [meta.fixture_id, meta.bookName, meta.market_id, meta.player_id, meta.selection, meta.selection_line].join('||')
    : '';
  const altsAvailable = !!meta && !!meta.bookName;

  // Reset to History whenever a different line is opened.
  useEffect(() => {
    setTab('history'); setAlts(null); setAltsError(false); setShowAll(false); setShowAllHistory(false);
  }, [altKey, open]);

  // Lazy fetch: only when the Alternates tab is selected.
  useEffect(() => {
    if (!open || tab !== 'alts' || alts || !altsAvailable) return;
    let cancelled = false;
    setAltsLoading(true); setAltsError(false);
    const params = new URLSearchParams({
      fixture_id: meta.fixture_id || '',
      sportsbook: meta.bookName || '',
      market_id: meta.market_id || '',
      player_id: meta.player_id || '',
      selection: meta.selection || '',
      selection_line: meta.selection_line || '',
    });
    fetch(`${ALT_ODDS_API_URL}?${params}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancelled) setAlts(d); })
      .catch((e) => { console.error('[ALT-ODDS] Fetch error:', e); if (!cancelled) setAltsError(true); })
      .finally(() => { if (!cancelled) setAltsLoading(false); });
    return () => { cancelled = true; };
  }, [open, tab, alts, altsAvailable, altKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  // Display fields
  const selectionDisplay = meta?.playerName || meta?.selection || '';
  const marketDisplay = meta?.marketDisplay || '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={phone}
      PaperProps={{
        sx: phone ? { backgroundColor: '#1a1a1a', color: '#ffffff', backgroundImage: 'none' } : {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          maxHeight: '85vh',
          maxWidth: '340px',
          borderRadius: '8px',
          border: '1px solid #333',
        }
      }}
    >
      <DialogContent sx={{ p: 0, ...(phone && { display: 'flex', flexDirection: 'column', overflow: 'hidden' }) }}>
        {/* ── Header ── */}
        <Box sx={{ px: phone ? 2 : 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #2a2a2a', flexShrink: 0 }}>
          <IconButton
            onClick={onClose}
            size={phone ? 'medium' : 'small'}
            sx={{ position: 'absolute', top: phone ? 6 : 8, right: phone ? 6 : 8, color: phone ? '#888' : '#555', '&:hover': { color: '#fff' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Line 1: Selection · Book logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3, pr: 4 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#aaa' }}>
              {selectionDisplay}
            </Typography>
            {meta?.bookLogo && (
              <img src={meta.bookLogo} alt="" style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 3 }} />
            )}
          </Box>

          {/* Line 2: Market */}
          {marketDisplay && (
            <Typography sx={{ fontSize: '0.75rem', color: '#888', mb: 0.3 }}>
              {marketDisplay}
            </Typography>
          )}

          {/* Line 3: League | away logo ABR @ home logo ABR */}
          {(meta?.league || meta?.awayTeam) && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.3 }}>
              {meta.league && (
                <>
                  <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{meta.league}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#444', mx: 0.3 }}>|</Typography>
                </>
              )}
              {meta.awayTeamLogo && (
                <img src={meta.awayTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
              )}
              {meta.awayTeam && (
                <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta.awayTeam}</Typography>
              )}
              <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>@</Typography>
              {meta.homeTeamLogo && (
                <img src={meta.homeTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
              )}
              {meta.homeTeam && (
                <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta.homeTeam}</Typography>
              )}
            </Box>
          )}

          {/* Line 4: Event start */}
          {meta?.eventStart && (
            <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>{fmtEventStart(meta.eventStart)}</Typography>
          )}
        </Box>

        {/* ── Tabs ── */}
        {altsAvailable && (
          <Box sx={{ display: 'flex', borderBottom: '1px solid #2a2a2a', flexShrink: 0 }}>
            {[['history', 'History'], ['alts', 'Alternates']].map(([k, label]) => (
              <Box key={k} onClick={() => setTab(k)} sx={{
                flex: 1, textAlign: 'center', py: phone ? 1.3 : 0.9, cursor: 'pointer',
                fontSize: '0.78rem', letterSpacing: '0.05em', textTransform: 'uppercase',
                color: tab === k ? '#fff' : '#666',
                borderBottom: tab === k ? '2px solid #ff1744' : '2px solid transparent',
                '&:hover': { color: '#fff' },
              }}>{label}</Box>
            ))}
          </Box>
        )}

        {/* ── Content (fixed height so switching tabs never resizes the modal).
             Phone: fills the screen and scrolls as one, chart included, so a
             sideways phone isn't left with a sliver of table under the chart. ── */}
        <Box sx={phone
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }
          : { height: CONTENT_HEIGHT, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {tab === 'alts' ? (
          <AltsTable
            alts={alts} loading={altsLoading} error={altsError} meta={meta}
            oddsFormat={oddsFormat} showAll={showAll} setShowAll={setShowAll}
          />
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: '#555' }} />
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>No history available</Typography>
          </Box>
        ) : (
          <>
            {/* Chart (capped so a wide full-screen phone doesn't blow it up;
                 a sideways phone keeps it short enough to see the table) */}
            <Box sx={{ flexShrink: 0, width: '100%', maxWidth: 480, mx: 'auto', '@media (max-height:500px)': { maxWidth: 340 } }}>
              <PriceChart data={data} oddsFormat={oddsFormat} />
            </Box>

            {/* Table */}
            <Box sx={phone
              ? { px: 1.5, pb: 1.5, pt: 1, flex: '1 0 auto', width: '100%', maxWidth: 480, mx: 'auto', boxSizing: 'border-box' }
              : { px: 1.5, pb: 1.5, pt: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH_STYLE}>Time</th>
                    <th style={{ ...TH_STYLE, textAlign: 'left', width: '1%', whiteSpace: 'nowrap' }}>Odds</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data].reverse().slice(0, showAllHistory ? undefined : HISTORY_ROWS).map((h, i) => {
                    const isLocked = h.event_type === 'locked';
                    const pts = h.points != null
                      ? (h.points % 1 === 0 ? `${h.points}\u00A0\u00A0` : `${h.points}`)
                      : '';
                    const price = h.price == null ? '-' : fmtPrice(h.price, oddsFormat);
                    const oddsText = pts !== '' ? `${pts} ${price}` : price;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                        <td style={{ ...TD_STYLE, color: '#999', whiteSpace: 'nowrap' }}>
                          {fmtTime(h.captured_at)}
                        </td>
                        <td style={{
                          ...TD_STYLE,
                          textAlign: 'right',
                          color: '#ccc',
                          fontWeight: 500,
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                          textDecoration: isLocked ? 'line-through' : 'none',
                        }}>
                          {oddsText}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {data.length > HISTORY_ROWS && (
                <ExpandToggle
                  expanded={showAllHistory}
                  onToggle={() => setShowAllHistory(!showAllHistory)}
                  label={`Show all ${data.length} updates`}
                />
              )}
            </Box>
          </>
        )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════
//  HISTORY CHART — stacked step charts on a shared time axis:
//    LINE (points)  — only when the line actually moved
//    ODDS (implied probability, labels in the user's odds format)
// ══════════════════════════════════════════════════════════

const impliedProb = (american) => {
  if (american == null || american === '' || Number.isNaN(Number(american))) return null;
  const a = Number(american);
  return a < 0 ? -a / (-a + 100) : 100 / (a + 100);
};
const probToAmerican = (p) => (p >= 0.5 ? Math.round((-100 * p) / (1 - p)) : Math.round((100 * (1 - p)) / p));

const CH_W = 300;
const CH_PAD = { l: 6, r: 58 };

function stepPath(pts, getVal, x, y, tEnd) {
  let d = '';
  let open = false;
  pts.forEach((pt, i) => {
    const v = getVal(pt);
    const nextT = i + 1 < pts.length ? pts[i + 1].t : tEnd;
    if (v == null) { open = false; return; }
    d += open ? `V${y(v).toFixed(1)}` : `M${x(pt.t).toFixed(1)},${y(v).toFixed(1)}`;
    d += `H${x(nextT).toFixed(1)}`;
    open = true;
  });
  return d;
}

function Panel({ pts, getVal, x, tEnd, top, height, title, fmtVal, pad, showNow }) {
  const vals = pts.map(getVal).filter((v) => v != null);
  let lo = Math.min(...vals), hi = Math.max(...vals);
  const p = Math.max((hi - lo) * 0.2, pad);
  const yLo = lo - p, yHi = hi + p;
  const y = (v) => top + 18 + (1 - (v - yLo) / (yHi - yLo)) * (height - 24);
  const cur = [...pts].reverse().find((pt) => getVal(pt) != null);
  const ticks = hi === lo ? [hi] : [hi, lo];
  return (
    <g>
      <text x={CH_PAD.l} y={top + 10} fontSize="10.5" fill="#666" fontFamily="monospace"
        letterSpacing="0.05em">{title}</text>
      {ticks.map((v, i) => (
        <g key={i}>
          <line x1={CH_PAD.l} x2={CH_W - CH_PAD.r} y1={y(v)} y2={y(v)} stroke="#2a2a2a" />
          <text x={CH_W - CH_PAD.r + 5} y={y(v) + 4} fontSize="11.5" fill="#888" fontFamily="monospace">
            {fmtVal(v)}
          </text>
        </g>
      ))}
      <path d={stepPath(pts, getVal, x, y, tEnd)} fill="none" stroke="#ff1744" strokeWidth="2" />
      {showNow && cur && <circle cx={x(tEnd)} cy={y(getVal(cur))} r="3.5" fill="#ff1744" />}
    </g>
  );
}

function PriceChart({ data, oddsFormat }) {
  const pts = (data || [])
    .map((h) => {
      const locked = h.event_type === 'locked';
      return {
        t: new Date(h.captured_at).getTime(),
        prob: locked ? null : impliedProb(h.price),
        line: locked || h.points == null || h.points === '' ? null : Number(h.points),
        locked,
      };
    })
    .filter((d) => Number.isFinite(d.t))
    .sort((a, b) => a.t - b.t);

  if (pts.filter((d) => d.prob != null).length < 2) return null;

  const lineMoved = new Set(pts.filter((d) => d.line != null).map((d) => d.line)).size > 1;
  const t0 = pts[0].t;
  const last = pts[pts.length - 1];
  const tEnd = last.locked ? last.t : Math.max(last.t, Date.now());
  const x = (t) => CH_PAD.l + ((t - t0) / Math.max(tEnd - t0, 1)) * (CH_W - CH_PAD.l - CH_PAD.r);

  const LINE_H = 72, ODDS_H = 92, AXIS_H = 18;
  const oddsTop = lineMoved ? LINE_H + 4 : 0;
  const totalH = oddsTop + ODDS_H + AXIS_H;
  const fmtShort = (t) => new Date(t).toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).replace(',', '');

  return (
    <Box sx={{ px: 1.5, pt: 1.2 }}>
      <svg viewBox={`0 0 ${CH_W} ${totalH}`} width="100%" style={{ display: 'block' }}>
        {lineMoved && (
          <Panel pts={pts} getVal={(d) => d.line} x={x} tEnd={tEnd} top={0} height={LINE_H}
            title="LINE" fmtVal={(v) => `${v}`} pad={0.5} showNow={!last.locked} />
        )}
        <Panel pts={pts} getVal={(d) => d.prob} x={x} tEnd={tEnd} top={oddsTop} height={ODDS_H}
          title="ODDS" fmtVal={(v) => fmtPrice(probToAmerican(v), oddsFormat)} pad={0.01}
          showNow={!last.locked} />
        <text x={CH_PAD.l} y={totalH - 3} fontSize="10.5" fill="#666" fontFamily="monospace">{fmtShort(t0)}</text>
        <text x={CH_W - CH_PAD.r} y={totalH - 3} fontSize="10.5" fill="#666" textAnchor="end"
          fontFamily="monospace">{last.locked ? fmtShort(tEnd) : 'now'}</text>
      </svg>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════
//  ALTERNATES TABLE
// ══════════════════════════════════════════════════════════

function AltsTable({ alts, loading, error, meta, oddsFormat, showAll, setShowAll }) {
  if (loading || (!alts && !error)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} sx={{ color: '#555' }} />
      </Box>
    );
  }
  const rungs = alts?.rungs || [];
  if (error || rungs.length <= 1) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>
          {error ? 'Could not load alternates' : 'No alternates offered'}
        </Typography>
      </Box>
    );
  }

  const clicked = Number(meta?.points);
  let anchor = rungs.findIndex((r) => Math.abs(r.line - clicked) < 1e-9);
  if (anchor < 0) anchor = Math.max(0, rungs.findIndex((r) => r.is_main));
  const windowed = !showAll && rungs.length > ALT_ROWS;
  const lo = windowed ? Math.max(0, anchor - ALT_WINDOW) : 0;
  const hi = windowed ? Math.min(rungs.length, anchor + ALT_WINDOW + 1) : rungs.length;
  const signed = rungs.some((r) => r.line < 0);
  const fmtLine = (x) => (signed && x > 0 ? `+${x}` : `${x}`);
  const cell = (s) => (s?.price == null ? '-' : fmtPrice(s.price, oddsFormat));

  return (
    <Box sx={{ px: 1.5, pb: 1.5, pt: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH_STYLE}>Line</th>
            <th style={{ ...TH_STYLE, textAlign: 'right' }}>{cap(alts.mine_label)}</th>
            <th style={{ ...TH_STYLE, textAlign: 'right' }}>{cap(alts.other_label) || ''}</th>
          </tr>
        </thead>
        <tbody>
          {rungs.slice(lo, hi).map((r, i) => {
            const isClicked = lo + i === anchor;
            return (
              <tr key={r.line} style={{
                borderBottom: '1px solid #222',
                backgroundColor: isClicked ? 'rgba(255,23,68,0.12)' : 'transparent',
              }}>
                <td style={{ ...TD_STYLE, ...NUM_STYLE, color: '#999' }}>
                  {fmtLine(r.line)}{r.is_main ? <span style={{ color: '#ff1744' }}> •</span> : null}
                </td>
                <td style={{ ...TD_STYLE, ...NUM_STYLE, textAlign: 'right', color: '#ccc', fontWeight: 500 }}>
                  {cell(r.mine)}
                </td>
                <td style={{ ...TD_STYLE, ...NUM_STYLE, textAlign: 'right', color: '#ccc', fontWeight: 500 }}>
                  {cell(r.other)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rungs.length > ALT_ROWS && (
        <ExpandToggle expanded={showAll} onToggle={() => setShowAll(!showAll)}
          label={`Show all ${rungs.length} lines`} />
      )}
    </Box>
  );
}

function ExpandToggle({ expanded, onToggle, label }) {
  return (
    <Typography onClick={onToggle} sx={{
      mt: 1, textAlign: 'center', fontSize: '0.75rem', color: '#888', cursor: 'pointer',
      '&:hover': { color: '#fff' },
    }}>
      {expanded ? 'Show fewer' : label}
    </Typography>
  );
}

const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace', whiteSpace: 'nowrap' };

const TH_STYLE = {
  position: 'sticky',      // header stays visible while the table scrolls
  top: 0,
  zIndex: 1,
  backgroundColor: '#1a1a1a',
  textAlign: 'left',
  padding: '6px 8px',
  color: '#555',
  fontSize: '0.8rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #2a2a2a',
};

const TD_STYLE = {
  padding: '6px 8px',
  fontSize: '0.85rem',
};